import {
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useState,
  startTransition,
  type FormEvent,
  type PointerEvent,
  type ReactNode,
} from "react"
import { useNavigate, useParams } from "react-router-dom"
import { CrownIcon, Share2Icon } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import type { PlayerView, SpectatorView } from "@/shared/game"
import { ALL_TOPIC_CATEGORIES, type TopicCategory } from "@/shared/game/types"
import { validatePlayerName } from "@/shared/validation/name"
import { ConnectionOverlay } from "../components/connection-overlay"
import { KickedScreen } from "../components/kicked-screen"
import { TerminalScreen } from "../components/terminal-screen"
import { ConnectionProvider, useConnectionState } from "../lib/connection-context"
import { getOrCreateDeviceToken } from "../lib/device"
import { formatActionError, formatNameError, formatTerminalReason } from "../lib/error-messages"
import { readStoredSession, RoomSocket, type RoomSocketStatus } from "../lib/ws-client"

export function RoomPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const roomCode = code?.toLocaleUpperCase() ?? ""
  const [state, setState] = useState<PlayerView | SpectatorView | null>(null)
  const [status, setStatus] = useState<RoomSocketStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [kickedReason, setKickedReason] = useState<string | null>(null)
  const [terminalReason, setTerminalReason] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const hasReceivedState = useRef(false)
  const socket = useMemo(() => (roomCode ? new RoomSocket(roomCode) : null), [roomCode])

  useEffect(() => {
    const online = () => setIsOnline(true)
    const offline = () => setIsOnline(false)
    window.addEventListener("online", online)
    window.addEventListener("offline", offline)
    return () => {
      window.removeEventListener("online", online)
      window.removeEventListener("offline", offline)
    }
  }, [])

  useEffect(() => {
    if (!roomCode || !socket) {
      navigate("/")
      return
    }

    const offState = socket.on("state", (payload) => {
      hasReceivedState.current = true
      setState(payload as PlayerView | SpectatorView)
    })
    const offStatus = socket.on("status", (payload) => setStatus(payload as RoomSocketStatus))
    const offError = socket.on("error", (payload) => {
      const message = payload as { reason?: string; error?: string }
      const reason = message.reason ?? message.error

      if (
        !hasReceivedState.current &&
        (reason === "ROOM_IN_PROGRESS" || reason === "NAME_TAKEN" || reason?.startsWith("NAME_"))
      ) {
        setError(reason)
        return
      }

      toast.error(formatActionError(reason))
    })
    const offExpired = socket.on("expired", (payload) => {
      setTerminalReason(String(payload))
    })
    const offKicked = socket.on("kicked", (payload) => {
      setKickedReason(String(payload))
    })
    const offLeft = socket.on("left", () => {
      // leaveRoom() has already wiped localStorage, closed the socket,
      // and set status to idle — just exit the route so the user lands
      // back on the chooser instead of looping in "reconnecting".
      navigate("/", { replace: true })
    })

    const connectTimer = readStoredSession(roomCode)
      ? window.setTimeout(() => socket.connect(), 0)
      : null

    return () => {
      if (connectTimer) {
        window.clearTimeout(connectTimer)
      }
      offState()
      offStatus()
      offError()
      offExpired()
      offKicked()
      offLeft()
      socket.close()
    }
  }, [navigate, roomCode, socket])

  const session = roomCode ? readStoredSession(roomCode) : null

  if (kickedReason) {
    return <KickedScreen reason={kickedReason} />
  }

  if (terminalReason) {
    return <TerminalScreen {...formatTerminalReason(terminalReason)} />
  }

  if (!session && !state) {
    return (
      <JoinRoomScreen
        roomCode={roomCode}
        socket={socket}
        error={error}
        onCancelInProgress={() => navigate("/")}
      />
    )
  }

  return (
    <ConnectionProvider
      value={{
        status,
        canAct: status === "authed" && isOnline,
        actionState: status === "authed" && isOnline ? "enabled" : "disabled",
        isOnline,
      }}
    >
      <ConnectionOverlay status={status} isOnline={isOnline} />
      <PhaseAnnouncer state={state} />
      <LobbyScreen
        state={state}
        status={status}
        currentPlayerId={session?.playerId ?? null}
        onStartRound={() => socket?.startRound()}
        socket={socket}
      />
    </ConnectionProvider>
  )
}

function JoinRoomScreen({
  roomCode,
  socket,
  error,
  onCancelInProgress,
}: {
  roomCode: string
  socket: RoomSocket | null
  error: string | null
  onCancelInProgress: () => void
}) {
  const [name, setName] = useState("")
  const [nameError, setNameError] = useState<string | null>(null)
  const normalizedError = error ? formatActionError(error) : null
  const roomInProgress = error === "ROOM_IN_PROGRESS"

  function joinRoom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const validation = validatePlayerName(name)

    if (!validation.ok) {
      setNameError(formatNameError(validation.reason))
      return
    }

    setNameError(null)
    socket?.join(validation.displayName, getOrCreateDeviceToken())
  }

  function spectateRoom() {
    const validation = validatePlayerName(name)

    if (!validation.ok) {
      setNameError(formatNameError(validation.reason))
      return
    }

    setNameError(null)
    socket?.spectate(validation.displayName, getOrCreateDeviceToken())
  }

  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
    >
      <header className="border-b-2 border-border px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
          Entrance · Room
        </p>
        <h1 className="mt-1 font-mono text-3xl font-bold uppercase tracking-[0.2em] text-foreground">
          {roomCode}
        </h1>
      </header>
      <section className="flex-1 px-5 py-6">
        <form onSubmit={joinRoom} className="flex flex-col gap-5">
          <label className="flex flex-col gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            Your name
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                const validation = validatePlayerName(event.target.value)
                setNameError(validation.ok ? null : formatNameError(validation.reason))
              }}
              aria-invalid={Boolean(nameError)}
              autoComplete="given-name"
              inputMode="text"
              name="player-name"
              placeholder="MAYA"
              className="h-12 bg-card text-base font-medium tracking-wide text-foreground placeholder:text-muted-foreground/50"
            />
            {nameError ? (
              <span className="text-sm normal-case tracking-normal text-primary">
                {nameError}
              </span>
            ) : null}
            {normalizedError && !roomInProgress ? (
              <span className="text-sm normal-case tracking-normal text-primary">
                {normalizedError}
              </span>
            ) : null}
          </label>
          <Button
            type="submit"
            className="h-14 bg-primary text-primary-foreground hover:bg-primary"
          >
            Enter lobby ↗
          </Button>
        </form>
      </section>
      <RoomInProgressDialog
        open={roomInProgress}
        onSpectate={spectateRoom}
        onCancel={onCancelInProgress}
      />
    </main>
  )
}

function RoomInProgressDialog({
  open,
  onSpectate,
  onCancel,
}: {
  open: boolean
  onSpectate: () => void
  onCancel: () => void
}) {
  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Round in session</AlertDialogTitle>
          <AlertDialogDescription>Enter as a silent spectator?</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onSpectate}
            className="bg-primary text-primary-foreground hover:bg-primary"
          >
            Spectate
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const CATEGORY_LABELS: Record<TopicCategory, string> = {
  CHARACTERS: "Characters",
  EVENTS: "Events",
  OBJECTS: "Objects",
  PARABLES: "Parables",
  VERSES: "Verses",
  PLACES: "Places",
}

/**
 * Category filter chip panel for the host. Mental model is exclude-from-
 * default: every category starts INCLUDED (filled oxblood); a tap REMOVES
 * the category from the draw pool (chip dims with strikethrough). This
 * matches every spam-filter / hide-this-calendar UI in the world — the
 * default state requires zero action, and the visual weight tracks reality.
 */
function CategoryFilterPanel({
  included,
  disabled,
  onChange,
  className = "mx-5 mt-4",
  description,
}: {
  included: TopicCategory[]
  disabled: boolean
  onChange: (next: TopicCategory[]) => void
  className?: string
  description?: string
}) {
  // Optimistic local state via React 19's useOptimistic. The server is the
  // source of truth, but every toggle requires a WS round-trip to the
  // Durable Object and back. Without optimism the chip visually does nothing
  // for ~50-300ms after the tap, which reads as "the click didn't register"
  // and provokes a second tap — which then toggles it BACK because both
  // taps land before the first echo. useOptimistic auto-snaps back to the
  // `included` prop on every re-render that doesn't sit inside a pending
  // transition, so if the server rejects the update we revert for free.
  const [effective, applyOptimistic] = useOptimistic<TopicCategory[], TopicCategory[]>(
    included,
    (_prev, next) => next
  )

  const includedSet = new Set(effective)
  const excludedCount = ALL_TOPIC_CATEGORIES.length - includedSet.size
  const excludedNames = ALL_TOPIC_CATEGORIES.filter((c) => !includedSet.has(c)).map(
    (c) => CATEGORY_LABELS[c]
  )

  function toggle(category: TopicCategory) {
    const next = new Set(includedSet)
    if (next.has(category)) {
      // Guard: at least one must remain. The server also rejects empty
      // payloads (AT_LEAST_ONE_CATEGORY_REQUIRED) — this blocks the toggle
      // at the click for instant feedback.
      if (next.size === 1) return
      next.delete(category)
    } else {
      next.add(category)
    }
    // Canonical ALL_TOPIC_CATEGORIES order so persisted state is stable
    // regardless of click order.
    const ordered = ALL_TOPIC_CATEGORIES.filter((c) => next.has(c))
    startTransition(() => {
      applyOptimistic(ordered)
      onChange(ordered)
    })
  }

  function resetAll() {
    const ordered = [...ALL_TOPIC_CATEGORIES]
    startTransition(() => {
      applyOptimistic(ordered)
      onChange(ordered)
    })
  }

  return (
    <section className={`${className} border-2 border-border bg-card px-4 py-3`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Topic pool ·{" "}
          <span className="text-foreground">
            Drawing from {includedSet.size} of {ALL_TOPIC_CATEGORIES.length}
          </span>
        </h2>
        {excludedCount > 0 ? (
          <button
            type="button"
            onClick={resetAll}
            disabled={disabled}
            aria-disabled={disabled}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent underline-offset-2 hover:underline disabled:text-muted-foreground"
          >
            Reset
          </button>
        ) : null}
      </div>
      {description ? <p className="prose-body mt-2 text-sm text-muted-foreground">{description}</p> : null}
      <div role="group" aria-label="Topic categories" className="mt-3 flex flex-wrap gap-2">
        {ALL_TOPIC_CATEGORIES.map((category) => {
          const isIncluded = includedSet.has(category)
          const isLastIncluded = isIncluded && includedSet.size === 1
          return (
            <button
              key={category}
              type="button"
              role="checkbox"
              aria-checked={isIncluded}
              aria-disabled={disabled || isLastIncluded}
              aria-label={
                isIncluded
                  ? `${CATEGORY_LABELS[category]} (included; tap to exclude)`
                  : `${CATEGORY_LABELS[category]} (excluded; tap to include)`
              }
              disabled={disabled || isLastIncluded}
              onClick={() => toggle(category)}
              className={`flex h-10 items-center justify-center border-2 px-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors disabled:cursor-not-allowed ${
                isIncluded
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground line-through opacity-60 hover:opacity-90"
              }`}
            >
              {CATEGORY_LABELS[category]}
            </button>
          )
        })}
      </div>
      {excludedCount > 0 ? (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Excluded · <span className="text-foreground">{excludedNames.join(", ")}</span>
        </p>
      ) : null}
    </section>
  )
}

function HostCategoryFilterPanel({
  state,
  socket,
  disabled,
  className,
  description,
}: {
  state: PlayerView | SpectatorView | null
  socket: RoomSocket | null
  disabled: boolean
  className?: string
  description?: string
}) {
  // Empty array from the server means "no filter" — render as all
  // categories included so the host sees the actual draw pool.
  // IMPORTANT: memoize the default array so React 19 useOptimistic inside
  // CategoryFilterPanel does not reset on every WebSocket state broadcast.
  const serverFilter = (state as PlayerView | null)?.categoryFilter
  const included = useMemo(
    () => (serverFilter && serverFilter.length > 0 ? serverFilter : [...ALL_TOPIC_CATEGORIES]),
    [serverFilter]
  )

  return (
    <CategoryFilterPanel
      included={included}
      disabled={disabled}
      onChange={(next) => socket?.updateCategoryFilter(next)}
      className={className}
      description={description}
    />
  )
}

function LobbyScreen({
  state,
  status,
  currentPlayerId,
  onStartRound,
  socket,
}: {
  state: PlayerView | SpectatorView | null
  status: RoomSocketStatus
  currentPlayerId: string | null
  onStartRound: () => void
  socket: RoomSocket | null
}) {
  const [kickTargetId, setKickTargetId] = useState<string | null>(null)
  const actionState = useConnectionState()
  const actionsDisabled = actionState === "disabled"

  if (state?.phase === "COUNTDOWN") {
    return <CountdownScreen state={state} socket={socket} />
  }

  if (state?.phase === "SECRET_REVEAL") {
    return <SecretRevealScreen state={state} socket={socket} />
  }

  if (state?.phase === "DISCUSSION") {
    return <DiscussionScreen state={state} socket={socket} />
  }

  if (state?.phase === "VOTING") {
    return <VotingScreen state={state} socket={socket} />
  }

  if (state?.phase === "RESULTS") {
    return <ResultsScreen state={state} socket={socket} />
  }

  const players = state?.players ?? []
  const currentPlayer = players.find((player) => player.playerId === currentPlayerId)
  const isHost = Boolean(currentPlayer?.isHost)
  const canStart = isHost && players.length >= 3 && !actionsDisabled
  const kickTarget = players.find((player) => player.playerId === kickTargetId) ?? null

  async function copyRoomCode() {
    if (!state) {
      return
    }

    await navigator.clipboard.writeText(state.roomCode)
    toast.success("Room code copied")
  }

  async function shareRoom() {
    if (!state) {
      return
    }

    const url = `${window.location.origin}/r/${state.roomCode}`

    if (navigator.share) {
      await navigator.share({ title: "Bible Imposter", text: state.roomCode, url })
      return
    }

    await navigator.clipboard.writeText(url)
    toast.success("Room link copied")
  }

  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
    >
      <header className="flex items-baseline justify-between border-b-2 border-border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
        <span>
          Phase · <span className="text-foreground">Lobby</span>
        </span>
        <span
          className={
            status === "authed"
              ? "text-foreground"
              : status === "reconnecting" || status === "connecting"
                ? "text-accent"
                : "text-primary"
          }
        >
          {status}
        </span>
      </header>

      <section className="flex flex-col gap-4 px-5 py-5">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Room code
          </p>
          <h1 className="mt-1 font-display text-5xl tracking-[0.15em] text-foreground">
            {state?.roomCode ?? "------"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Share to invite. <span className="text-foreground">{state?.imposterCount ?? 1} imposter{(state?.imposterCount ?? 1) > 1 ? "s" : ""}</span>
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-12 flex-1 bg-transparent text-foreground hover:bg-card"
            onClick={copyRoomCode}
            disabled={!state}
          >
            Copy code
          </Button>
          <Button
            variant="outline"
            className="h-12 flex-1 bg-transparent text-foreground hover:bg-card"
            onClick={shareRoom}
            disabled={!state}
          >
            <Share2Icon data-icon="inline-start" />
            Share
          </Button>
        </div>
      </section>

      <section className="px-5" aria-label="Players">
        <div className="flex items-baseline justify-between pb-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Roster
          </p>
          <p className="font-mono text-xs font-bold tabular-nums text-foreground">
            {players.length}
          </p>
        </div>
        <ul className="divide-y-2 divide-border border-2 border-border bg-card">
          {players.map((player) => (
            <li
              key={player.playerId}
              className="flex items-center justify-between gap-3 px-3 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{player.name}</p>
                {player.playerId === currentPlayerId ? (
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    You
                  </p>
                ) : null}
              </div>
              {player.isHost ? (
                <span className="inline-flex items-center gap-1 border-2 border-accent px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                  <CrownIcon data-icon="inline-start" />
                  Host
                </span>
              ) : null}
              {isHost && player.playerId !== currentPlayerId ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionsDisabled}
                  aria-disabled={actionsDisabled}
                  onClick={() => setKickTargetId(player.playerId)}
                  className="h-8 bg-transparent px-2 text-xs text-foreground hover:bg-background"
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      {state?.spectators.length ? (
        <details className="mx-5 mt-4 border-2 border-border bg-card px-4 py-3">
          <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Spectators · <span className="text-foreground">{state.spectators.length}</span>
          </summary>
          <div className="mt-3 flex flex-col gap-2">
            {state.spectators.map((spectator) => (
              <p key={spectator.playerId} className="text-sm">
                {spectator.name}
              </p>
            ))}
          </div>
        </details>
      ) : null}

      {isHost ? (
        <HostCategoryFilterPanel
          state={state}
          socket={socket}
          disabled={actionsDisabled}
        />
      ) : null}

      <div className="mt-auto px-5 py-5">
        {isHost ? (
          <div className="flex flex-col gap-2">
            <Button
              className="h-14 bg-primary text-primary-foreground hover:bg-primary disabled:bg-muted disabled:text-muted-foreground"
              disabled={!canStart}
              aria-disabled={!canStart}
              onClick={onStartRound}
            >
              {canStart ? "Begin round ↗" : `Waiting · ${Math.max(0, 3 - players.length)} more needed`}
            </Button>
          </div>
        ) : (
          <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Standby · Host starts round
          </p>
        )}
      </div>

      <KickPlayerDialog
        playerName={kickTarget?.name ?? ""}
        open={Boolean(kickTarget)}
        disabled={actionsDisabled}
        onCancel={() => setKickTargetId(null)}
        onConfirm={() => {
          if (kickTarget) {
            socket?.kickPlayer(kickTarget.playerId)
          }
          setKickTargetId(null)
        }}
      />
    </main>
  )
}

function KickPlayerDialog({
  playerName,
  open,
  onCancel,
  onConfirm,
  disabled,
}: {
  playerName: string
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  disabled: boolean
}) {
  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? onCancel() : undefined)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {playerName}?</AlertDialogTitle>
          <AlertDialogDescription>
            They will leave the room and lose their place.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={disabled}
            aria-disabled={disabled}
            className="bg-primary text-primary-foreground hover:bg-primary"
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function PhaseAnnouncer({ state }: { state: PlayerView | SpectatorView | null }) {
  const message = phaseAnnouncement(state)

  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" data-testid="phase-live-region">
      {message}
    </div>
  )
}

function phaseAnnouncement(state: PlayerView | SpectatorView | null) {
  if (!state) {
    return ""
  }

  switch (state.phase) {
    case "LOBBY":
      return "Lobby. Waiting for players."
    case "COUNTDOWN":
      return "Round started. Get ready."
    case "SECRET_REVEAL":
      return "Reveal phase. Check your role."
    case "DISCUSSION":
      return "Discussion phase. Talk it out, then open the vote."
    case "VOTING":
      return "Voting phase. Choose a player."
    case "RESULTS":
      return state.round?.outcome === "TEAM_WIN" ? "Results: the team won." : "Results: the bluff won."
    default:
      return ""
  }
}

function ScreenShell({
  phase,
  children,
  roundNumber,
}: {
  phase: PlayerView["phase"]
  children: ReactNode
  roundNumber?: number
}) {
  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
      data-phase={phase}
      data-testid={`screen-${phase}`}
    >
      <PhaseHeader phase={phase} roundNumber={roundNumber} />
      <div className="flex flex-1 flex-col gap-4 px-5 py-5">{children}</div>
    </main>
  )
}

const PHASE_LABEL: Record<PlayerView["phase"], string> = {
  LOBBY: "Lobby",
  COUNTDOWN: "Countdown",
  SECRET_REVEAL: "Reveal",
  DISCUSSION: "Discussion",
  VOTING: "Verdict",
  RESULTS: "Outcome",
}

function PhaseHeader({ phase, roundNumber }: { phase: PlayerView["phase"]; roundNumber?: number }) {
  const label = PHASE_LABEL[phase] ?? phase
  return (
    <header className="flex items-baseline justify-between border-b-2 border-border bg-background px-5 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
      <h1 className="sr-only">
        {label}
        {roundNumber ? ` — Round ${roundNumber}` : ""}
      </h1>
      <span aria-hidden="true">
        Phase ·{" "}
        <span className="text-foreground">{label}</span>
      </span>
      {roundNumber ? (
        <span aria-hidden="true">
          Round{" "}
          <span className="text-foreground">{String(roundNumber).padStart(2, "0")}</span>
        </span>
      ) : null}
    </header>
  )
}

function CountdownScreen({
  state,
  socket,
}: {
  state: PlayerView | SpectatorView
  socket: RoomSocket | null
}) {
  const [now, setNow] = useState(Date.now())
  const endsAt = state.round?.countdownEndsAt ?? now
  const seconds = Math.max(0, Math.ceil((endsAt - now) / 1000))
  const isPlayer = isPlayerView(state)
  const isHost = isPlayer ? state.you.isHost : false
  const actionsDisabled = useConnectionState() === "disabled"

  useEffect(() => {
    let frame = requestAnimationFrame(function tick() {
      setNow(Date.now())
      frame = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  return (
    <ScreenShell phase="COUNTDOWN" roundNumber={state.currentRoundNumber}>
      <div className="my-auto flex flex-col items-center gap-2 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          T-minus
        </p>
        <p className="font-mono text-[12rem] font-bold leading-none tabular-nums text-accent">
          {String(seconds).padStart(2, "0")}
        </p>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          {isHost ? "Host may abort below" : "Brace yourself"}
        </p>
      </div>
      {isHost ? (
        <Button
          variant="outline"
          className="mt-auto h-12 bg-transparent text-foreground hover:bg-card"
          disabled={actionsDisabled}
          aria-disabled={actionsDisabled}
          onClick={() => socket?.cancelCountdown()}
        >
          Abort countdown
        </Button>
      ) : null}
    </ScreenShell>
  )
}

function SecretRevealScreen({
  state,
  socket,
}: {
  state: PlayerView | SpectatorView
  socket: RoomSocket | null
}) {
  const isPlayer = isPlayerView(state)
  const isImposter = isPlayer ? state.you.isImposter : null
  const isHost = isPlayer ? state.you.isHost : false
  const actionsDisabled = useConnectionState() === "disabled"

  return (
    <ScreenShell phase="SECRET_REVEAL" roundNumber={state.currentRoundNumber}>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
        Sealed envelope · Read in private
      </p>
      <HoldToReveal>
        {isImposter ? (
          <>
            <p className="font-display text-3xl uppercase tracking-[0.2em] text-primary">
              Imposter
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              You do not know the topic
            </p>
            <p className="mt-2 text-sm">Bluff your way through. Blend in.</p>
          </>
        ) : (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Topic
            </p>
            <p className="mt-2 font-display text-2xl uppercase tracking-[0.1em] text-foreground">
              {state.round?.topicTitle ?? "???"}
            </p>
            {state.round?.topicReference ? (
              <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                {state.round.topicReference}
              </p>
            ) : null}
          </>
        )}
      </HoldToReveal>
      {isHost ? (
        <Button
          className="mt-auto h-14 bg-primary text-primary-foreground hover:bg-primary"
          disabled={actionsDisabled}
          aria-disabled={actionsDisabled}
          onClick={() => socket?.beginDiscussion()}
        >
          Open discussion ↗
        </Button>
      ) : (
        <p className="mt-auto text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          Standby · Host advances
        </p>
      )}
    </ScreenShell>
  )
}

function DiscussionScreen({
  state,
  socket,
}: {
  state: PlayerView | SpectatorView
  socket: RoomSocket | null
}) {
  const round = state.round
  const eliminatedIds = round?.eliminatedPlayerIds ?? []
  const variant = round?.variant ?? "small"
  const subRound = round?.subRound ?? 1
  const isPlayer = isPlayerView(state)
  const isImposter = isPlayer ? state.you.isImposter : null
  const isEliminated = isPlayer && eliminatedIds.includes(state.you.playerId)
  const activePlayers = state.players.filter((player) =>
    round?.activePlayerIds.includes(player.playerId),
  )
  const survivors = activePlayers.filter((p) => !eliminatedIds.includes(p.playerId))
  const eliminatedPlayers = activePlayers.filter((p) => eliminatedIds.includes(p.playerId))
  const actionsDisabled = useConnectionState() === "disabled"
  const canOpenVote = isPlayer && !isEliminated

  return (
    <ScreenShell phase="DISCUSSION" roundNumber={state.currentRoundNumber}>
      <div className="flex flex-col gap-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Your card
        </p>
        <HoldToReveal variant="peek">
          {isImposter ? (
            <>
              <p className="font-mono text-xl font-black uppercase tracking-[0.25em] text-primary">
                Imposter
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Bluff. Blend in.
              </p>
            </>
          ) : (
            <>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                Topic
              </p>
              <p className="mt-1 font-mono text-lg font-black uppercase tracking-wider text-foreground">
                {state.round?.topicTitle ?? "???"}
              </p>
              {state.round?.topicReference ? (
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  {state.round.topicReference}
                </p>
              ) : null}
            </>
          )}
        </HoldToReveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Speak your clues out loud. The phone never sees them.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            At the table
          </p>
          {variant === "standard" ? (
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-foreground">
              Sub-round {subRound}
            </p>
          ) : null}
        </div>

        <ul className="divide-y-2 divide-border border-2 border-border bg-card">
          {survivors.map((player) => (
            <li
              key={player.playerId}
              className="flex items-center justify-between gap-3 px-3 py-3 text-sm"
            >
              <span className="font-medium">{player.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Active
              </span>
            </li>
          ))}
        </ul>

        {eliminatedPlayers.length > 0 ? (
          <ul className="divide-y-2 divide-border border-2 border-border/60 bg-muted/40">
            {eliminatedPlayers.map((player) => (
              <li
                key={player.playerId}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm opacity-70"
              >
                <span className="line-through">{player.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Eliminated
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {isEliminated ? (
        <div className="mt-auto border-2 border-border bg-muted/60 p-3 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            You were eliminated
          </p>
          <p className="mt-1 text-sm">Watch the rest of the round play out.</p>
        </div>
      ) : canOpenVote ? (
        <Button
          type="button"
          disabled={actionsDisabled}
          aria-disabled={actionsDisabled}
          onClick={() => socket?.openVote()}
          className="mt-auto h-14 bg-primary text-base font-bold uppercase tracking-[0.2em] text-primary-foreground hover:bg-primary"
        >
          Open vote
        </Button>
      ) : (
        <p className="mt-auto text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          Spectating
        </p>
      )}
    </ScreenShell>
  )
}

function VotingScreen({
  state,
  socket,
}: {
  state: PlayerView | SpectatorView
  socket: RoomSocket | null
}) {
  const round = state.round
  const isPlayer = isPlayerView(state)
  const myVote = isPlayer ? state.you.myVote : undefined
  const eliminatedIds = round?.eliminatedPlayerIds ?? []
  const variant = round?.variant ?? "small"
  const subRound = round?.subRound ?? 1
  const activePlayers = state.players.filter((player) =>
    round?.activePlayerIds.includes(player.playerId),
  )
  const ballotPlayers = activePlayers.filter((p) => !eliminatedIds.includes(p.playerId))
  const eliminatedPlayers = activePlayers.filter((p) => eliminatedIds.includes(p.playerId))
  const voted = ballotPlayers.filter((player) => player.hasVoted).length
  const isEliminated = isPlayer && eliminatedIds.includes(state.you.playerId)
  const actionsDisabled = useConnectionState() === "disabled"

  return (
    <ScreenShell phase="VOTING" roundNumber={state.currentRoundNumber}>
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Secret ballot
        </p>
        {variant === "standard" ? (
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-foreground">
            Sub-round {subRound}
          </p>
        ) : null}
      </div>

      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Ballot · Cast one
        </p>
        <p className="font-mono text-sm font-bold tabular-nums text-foreground">
          {voted}/{ballotPlayers.length}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {ballotPlayers.map((player) => {
          const self = isPlayer && player.playerId === state.you.playerId
          const disabled =
            actionsDisabled || !isPlayer || self || isEliminated || Boolean(myVote)
          const baseClass =
            "h-14 w-full justify-between bg-card text-foreground hover:bg-card text-left"
          const button = (
            <Button
              key={player.playerId}
              variant="outline"
              disabled={disabled}
              aria-disabled={disabled}
              className={baseClass}
            >
              <span>{player.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {self ? "Self" : "Accuse →"}
              </span>
            </Button>
          )
          if (disabled) {
            return button
          }
          return (
            <AlertDialog key={player.playerId}>
              <AlertDialogTrigger asChild>{button}</AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Accuse {player.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Your vote is final once cast.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => socket?.submitVote(player.playerId)}
                    className="bg-primary text-primary-foreground hover:bg-primary"
                  >
                    Cast vote
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        })}
      </div>

      {eliminatedPlayers.length > 0 ? (
        <ul className="divide-y-2 divide-border border-2 border-border/60 bg-muted/40">
          {eliminatedPlayers.map((player) => (
            <li
              key={player.playerId}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm opacity-70"
            >
              <span className="line-through">{player.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Eliminated · cannot vote
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {isEliminated ? (
        <p className="mt-auto text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          You were eliminated · spectating
        </p>
      ) : myVote ? (
        <p className="mt-auto text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          Vote sealed
        </p>
      ) : null}
    </ScreenShell>
  )
}

function ResultsScreen({
  state,
  socket,
}: {
  state: PlayerView | SpectatorView
  socket: RoomSocket | null
}) {
  const isPlayer = isPlayerView(state)
  const isHost = isPlayer ? state.you.isHost : false
  const actionsDisabled = useConnectionState() === "disabled"
  const imposterNames = state.players
    .filter((player) => state.round?.imposterIds.includes(player.playerId))
    .map((player) => player.name)
    .join(", ")

  const outcome = state.round?.outcome ?? null
  const imposterWon = outcome === "IMPOSTER_WIN"
  const teamWon = outcome === "TEAM_WIN"
  const cancelled = outcome === "CANCELLED"
  const verdict = cancelled
    ? "Round cancelled"
    : imposterWon
      ? "Imposter wins"
      : teamWon
        ? "Team wins"
        : "Pending"
  const verdictHighlight = imposterWon

  return (
    <ScreenShell phase="RESULTS" roundNumber={state.currentRoundNumber}>
      <div
        className="flex flex-col gap-2 border-2 border-border p-5"
        style={{
          background: verdictHighlight ? "var(--primary)" : "var(--card)",
          color: verdictHighlight ? "var(--primary-foreground)" : "var(--foreground)",
          boxShadow: "var(--shadow-brutal)",
        }}
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] opacity-80">
          Verdict
        </p>
        <p className="font-mono text-3xl font-bold uppercase tracking-tight leading-none">
          {verdict}
        </p>
        <div className="brutalist-rule mt-2 opacity-50" />
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] opacity-80">
          Topic
        </p>
        <p className="text-xl font-medium">{state.round?.topicTitle}</p>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] opacity-80">
          Imposter
        </p>
        <p className="font-mono text-base font-bold uppercase tracking-wider">
          {imposterNames || "—"}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          Ballot record
        </p>
        <ul className="divide-y-2 divide-border border-2 border-border bg-card">
          {Object.entries(state.round?.votes ?? {}).map(([voterId, suspectId]) => (
            <li
              key={voterId}
              className="flex items-center justify-between gap-3 px-3 py-3 text-sm"
            >
              <span>{nameFor(state, voterId)}</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                voted →
              </span>
              <span className="font-medium">{nameFor(state, suspectId)}</span>
            </li>
          ))}
        </ul>
      </div>

      {isHost ? (
        <HostCategoryFilterPanel
          state={state}
          socket={socket}
          disabled={actionsDisabled}
          className="mt-2"
          description="Applies to the next round only. The finished round keeps its topic."
        />
      ) : null}

      <div className="mt-auto flex flex-col gap-2">
        {isHost ? (
          <Button
            className="h-14 bg-primary text-primary-foreground hover:bg-primary"
            disabled={actionsDisabled}
            aria-disabled={actionsDisabled}
            onClick={() => socket?.startRound()}
          >
            Open next round ↗
          </Button>
        ) : (
          <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Standby · Host advances
          </p>
        )}
        <Button
          variant="outline"
          className="h-12 bg-transparent text-foreground hover:bg-card"
          disabled={actionsDisabled}
          aria-disabled={actionsDisabled}
          onClick={() => socket?.leaveRoom()}
        >
          Leave room
        </Button>
      </div>
    </ScreenShell>
  )
}

function HoldToReveal({
  children,
  compact = false,
  variant = "full",
}: {
  children: ReactNode
  compact?: boolean
  /** Visual size of the card. "full" = secret reveal screen centerpiece, "peek" = persistent peek card in DISCUSSION/VOTING. */
  variant?: "full" | "peek"
}) {
  const [holding, setHolding] = useState(false)
  const pointerId = useRef<number | null>(null)

  useEffect(() => {
    if (!holding) {
      return
    }

    const timeout = window.setTimeout(() => setHolding(false), 5000)
    return () => window.clearTimeout(timeout)
  }, [holding])

  useEffect(() => {
    const hide = () => setHolding(false)
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        setHolding(false)
      }
    }
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("blur", hide)
    window.addEventListener("pagehide", hide)
    return () => {
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("blur", hide)
      window.removeEventListener("pagehide", hide)
    }
  }, [])

  function reveal(event: PointerEvent<HTMLDivElement>) {
    pointerId.current = event.pointerId
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // Synthetic pointer events in automated tests do not always create an active pointer.
    }
    setHolding(true)
  }

  function hide(event: PointerEvent<HTMLDivElement>) {
    if (pointerId.current === event.pointerId) {
      setHolding(false)
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      pointerId.current = null
    }
  }

  return (
    <>
      {holding && !compact && variant === "full" ? (
        <div className="fixed inset-0 z-10 bg-[rgba(0,0,0,0.92)]" />
      ) : null}
      {compact ? (
        <span
          role="button"
          tabIndex={0}
          aria-pressed={holding}
          aria-label="Hold to reveal topic"
          data-testid="topic-hold"
          data-revealed={holding ? "true" : "false"}
          className="inline-flex min-w-[16ch] items-center justify-center border-2 border-accent px-3 py-1 font-mono text-base font-bold uppercase tracking-wider text-accent touch-none select-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
          style={{
            filter: holding ? "blur(0)" : "blur(8px)",
            backgroundColor: holding ? "transparent" : "var(--secondary)",
          }}
          onPointerDown={reveal}
          onPointerUp={hide}
          onPointerCancel={hide}
          onKeyDown={(event) => {
            if (event.key === " " || event.key === "Enter") {
              event.preventDefault()
              setHolding(true)
            }
          }}
          onKeyUp={(event) => {
            if (event.key === " " || event.key === "Enter") setHolding(false)
          }}
        >
          {holding ? children : "[ hold to read ]"}
        </span>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-pressed={holding}
          aria-label="Hold to reveal your role"
          data-testid="role-card-wrap"
          data-variant={variant}
          className={
            variant === "full"
              ? "relative z-20 mx-auto mt-auto mb-auto w-[min(80vw,22rem)] border-4 border-foreground bg-card touch-none select-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
              : "relative z-20 w-full border-4 border-foreground bg-card touch-none select-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
          }
          style={{ boxShadow: "var(--shadow-brutal)" }}
          onPointerDown={reveal}
          onPointerUp={hide}
          onPointerCancel={hide}
          onKeyDown={(event) => {
            if (event.key === " " || event.key === "Enter") {
              event.preventDefault()
              setHolding(true)
            }
          }}
          onKeyUp={(event) => {
            if (event.key === " " || event.key === "Enter") setHolding(false)
          }}
        >
          <div
            className={
              variant === "full"
                ? "relative flex min-h-80 items-center justify-center px-6 py-12 text-center"
                : "relative flex min-h-44 items-center justify-center px-5 py-8 text-center"
            }
          >
            <div
              data-testid="role-card"
              data-revealed={holding ? "true" : "false"}
              className="w-full px-2"
              style={{ filter: holding ? "blur(0)" : "blur(24px)" }}
            >
              {children}
            </div>
            <div
              data-testid="role-overlay"
              aria-hidden={holding ? "true" : "false"}
              className={
                variant === "full"
                  ? "absolute inset-0 flex flex-col items-center justify-center gap-6 bg-card"
                  : "absolute inset-0 flex flex-col items-center justify-center gap-3 bg-card"
              }
              style={{ opacity: holding ? 0 : 1, transition: "opacity 80ms linear" }}
            >
              <div
                aria-hidden="true"
                className={
                  variant === "full"
                    ? "relative flex h-20 w-20 items-center justify-center border-4 border-foreground"
                    : "relative flex h-12 w-12 items-center justify-center border-2 border-foreground"
                }
              >
                <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 bg-foreground" />
                <span className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 bg-foreground" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.4em] text-muted-foreground">
                  Sealed
                </p>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-foreground">
                  Press &amp; hold to reveal
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function nameFor(state: PlayerView | SpectatorView, playerId: string) {
  return state.players.find((player) => player.playerId === playerId)?.name ?? playerId
}

function isPlayerView(state: PlayerView | SpectatorView): state is PlayerView {
  return "isHost" in state.you
}
